/**
 * Rastreamento de erros do front.
 *
 * Captura erros não tratados (`window.onerror`, `unhandledrejection`) e erros
 * de renderização do React (via `ErrorBoundary`) e os reporta ao backend em
 * `POST /api/client-errors` (endpoint opcional de telemetria). O envio é
 * best-effort: uma falha no reporte JAMAIS interrompe a aplicação.
 */

import { getToken } from "./auth";

/** Descreve um erro capturado a ser reportado. */
export interface ErrorReport {
  kind: "window" | "unhandledrejection" | "react";
  message: string;
  stack?: string | undefined;
  componentStack?: string | undefined;
  url?: string | undefined;
}

interface ClientErrorPayload {
  message: string;
  stack: string;
  url: string;
  userAgent: string;
  component: string;
  severity: string;
}

const ERROR_ENDPOINT = "/api/client-errors";

const isDevelopment = typeof window !== "undefined" && window.location.hostname === "localhost";

function componentFor(kind: ErrorReport["kind"]): string {
  switch (kind) {
    case "react":
      return "react-error-boundary";
    case "unhandledrejection":
      return "unhandled-promise-rejection";
    default:
      return "window-error";
  }
}

function toPayload(report: ErrorReport): ClientErrorPayload {
  const stack = report.componentStack
    ? `${report.stack ?? ""}\n\nComponentStack:${report.componentStack}`.trim()
    : (report.stack ?? "");
  return {
    message: report.message,
    stack,
    url: report.url ?? "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    component: componentFor(report.kind),
    severity: "error",
  };
}

/**
 * Envia o relatório ao backend. Sem sessão ativa, o reporte é descartado (não
 * há como correlacioná-lo). `keepalive` permite sobreviver à navegação; falhas
 * de rede são silenciadas — o rastreamento nunca derruba a aplicação.
 */
function dispatch(report: ErrorReport): void {
  const token = getToken();
  if (!token) {
    return;
  }
  try {
    void fetch(ERROR_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(toPayload(report)),
      keepalive: true,
    }).catch(() => {
      // Falha de envio silenciada de propósito.
    });
  } catch {
    // Ambiente sem rede / API indisponível — ignora.
  }
}

/** Reporta um erro capturado. Em desenvolvimento, também loga no console. */
export function reportError(report: ErrorReport): void {
  const enriched: ErrorReport = {
    ...report,
    url: report.url ?? (typeof window !== "undefined" ? window.location.href : undefined),
  };
  if (isDevelopment) {
    console.error(`[error-tracking:${enriched.kind}]`, enriched.message, enriched);
  }
  dispatch(enriched);
}

let installed = false;

/**
 * Instala os listeners globais de captura de erros não tratados. Idempotente —
 * chamado uma vez no bootstrap (`main.tsx`).
 */
export function installErrorTracking(): void {
  if (installed || typeof window === "undefined") {
    return;
  }
  installed = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    reportError({
      kind: "window",
      message: event.message || "Erro não tratado",
      stack: event.error instanceof Error ? event.error.stack : undefined,
    });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    reportError({
      kind: "unhandledrejection",
      message:
        reason instanceof Error
          ? reason.message
          : typeof reason === "string"
            ? reason
            : "Promise rejeitada sem tratamento",
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });
}
